import { Box } from 'components/Box'
import { Button } from 'components/Button'
import { Checkbox } from 'components/Checkbox'
import { Icon } from 'components/Icon'
import { RadioGroup } from 'components/RadioGroup/RadioGroup'
import { Select } from 'components/Select'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { TextInput } from 'components/TextInput'
import { Tooltip } from 'components/Tooltip'
import { InputLabel } from 'features/MealPlan/components/InputLabel'
import { Loader } from 'features/components/Loader'
import { useDateFormat } from 'hooks/useDateFormat'
import { useRouter } from 'hooks/useRouter'
import { useTranslation } from 'hooks/useTranslation'
import { BirthDate, PetGenders, PetKinds } from 'models/Pet'
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import { getDateDiffInYearsAndMonths, getDaysInCurrentMonth } from 'utils/date'
import {
  PetAttempt,
  PetAttemptStep1,
  useSubmitPetAttempt,
} from '../../api/mutations/useSubmitPetAttempt'
import { useFetchPetAttempt } from '../../api/queries/useFetchPetAttempt'
import { MealPlanLayout } from '../../components/MealPlanLayout'
import { MealPlanQuestionnaireCard } from '../../components/MealPlanLayout/MealPlanLayout.styles'
import { getApproximatedDateOfBirth } from '../../utils/getApproximateDateOfBirth'
import { Step1Root } from './Step1.styles'
import { RadioOption } from './components/RadioOption/RadioOption'
import { TextRadioOption } from './components/TextRadioOption'
import { fbConversion } from '../../../../api/analytics/analytics'

type Step1Props = {}

export const Step1 = (_props: Step1Props) => {
  const { t } = useTranslation()
  const { format } = useDateFormat()

  const { data: petAttemptStep1, status } = useFetchPetAttempt({ retry: false })
  const { submitPetAttempt } = useSubmitPetAttempt()
  const router = useRouter()

  const [loading, setLoading] = useState(true)
  const [petProfile, setPetProfile] = useState<Partial<PetAttemptStep1>>({
    isSpayed: false,
    kind: PetKinds.DOG,
  })

  const [knownBirthDate, setKnownBirthDate] = useState(false)
  const [birthDate, setBirthDate] = useState<BirthDate>()

  const [age, setAge] = useState<{ years?: number; months?: number }>()

  const [daysInMonth, setDaysInMonth] = useState(31)

  const { MONTHS_OPTIONS, DAYS_OPTIONS, YEARS_OPTIONS, currentYear } = useMemo(() => {
    const MONTHS_OPTIONS = Array.from({ length: 12 }, (_, i) => `${i + 1}`)

    const DAYS_OPTIONS = Array.from({ length: daysInMonth }, (_, i) => `${i + 1}`)

    const currentYear = new Date().getFullYear()
    const YEARS_OPTIONS = Array.from({ length: 30 }, (_, i) => `${i + currentYear - 29}`).reverse()

    return { MONTHS_OPTIONS, DAYS_OPTIONS, YEARS_OPTIONS, currentYear }
  }, [daysInMonth])

  useEffect(() => {
    fbConversion('InitiateWizard')
  }, [])

  useEffect(() => {
    if (status === 'loading') return
    setLoading(false)

    if (!petAttemptStep1) return

    setPetProfile({ ...petAttemptStep1, isSpayed: petAttemptStep1.isSpayed ?? false })

    if (!petAttemptStep1.birthDate) return

    setAge({
      years: getDateDiffInYearsAndMonths(petAttemptStep1.birthDate).years,
      months: getDateDiffInYearsAndMonths(petAttemptStep1.birthDate).months,
    })

    setKnownBirthDate(!!petAttemptStep1.birthDate)

    setBirthDate({
      year: parseInt(format(petAttemptStep1.birthDate, 'YEAR')),
      month: parseInt(format(petAttemptStep1.birthDate, 'MONTH_NUMBER')),
      day: parseInt(format(petAttemptStep1.birthDate, 'DAY')),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, petAttemptStep1])

  useEffect(() => {
    if (!birthDate?.month) return

    setDaysInMonth(getDaysInCurrentMonth(birthDate.year || currentYear, birthDate.month))
  }, [birthDate?.month, birthDate?.year, currentYear])

  if (loading) return <Loader />

  const ageFilled =
    (knownBirthDate && birthDate?.year && birthDate?.month && birthDate?.day) ||
    (!knownBirthDate && (age?.years || age?.months))

  const allFieldsFilled = petProfile.name && petProfile.gender && ageFilled

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPetProfile({
      ...petProfile,
      [e.target.name]: e.target.value,
    })
  }

  const handleAgeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAge({ ...age, [e.target.name]: parseInt(e.target.value) })
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!allFieldsFilled) return

    const submitData = petProfile
    petProfile.kind = PetKinds.DOG

    submitData.birthDate = knownBirthDate
      ? new Date(birthDate!.year!, birthDate!.month! - 1, birthDate!.day!).toISOString()
      : getApproximatedDateOfBirth(age?.years || 0, age?.months || 0)

    submitPetAttempt(
      {
        ...submitData,
        utmCampaign: Object.keys(router.query).length > 0 ? router.query : undefined,
      } as Required<PetAttemptStep1 & Pick<PetAttempt, 'utmCampaign'>>,
      {
        onSuccess: (data) => {
          router.replace('/meal-plan/step-2')
        },
        onError: (err) => {
          // console.log(err)
        },
      }
    )
  }

  return (
    <MealPlanLayout step={1}>
      <Step1Root>
        <MealPlanQuestionnaireCard>
          <Stack
            direction="column"
            alignItems="center"
            gap={{ '@initial': '4', '@bp2': '7' }}
            as="form"
            onSubmit={handleSubmit}
          >
            <Text
              size={{ '@initial': 'h4', '@bp2': 'h3' }}
              as="h1"
              weight="bold"
              color="primary-800"
            >
              {t('meal-plan:step-1.title')}
            </Text>

            <TextInput
              outerLabel={(props) => (
                <Stack alignItems="center">
                  <InputLabel {...props} />
                  <Tooltip instant side="top" text={t('meal-plan:step-1.name-tooltip')}>
                    <Box css={{ display: 'flex', alignItems: 'center' }}>
                      <Icon name="information-circle-line" color="primary-800" />
                    </Box>
                  </Tooltip>
                </Stack>
              )}
              name="name"
              defaultValue={petProfile.name}
              onChange={handleChange}
              displayLabel={false}
              label={t('meal-plan:step-1.name-question')}
              placeholder={t('meal-plan:step-1.name')}
              removeDiacritics={true}
            />

            <Stack gap="4" direction="column">
              <Stack direction="column" alignItems="center" gap={{ '@initial': '1', '@bp2': '2' }}>
                <Text
                  size={{ '@initial': 'body', '@bp2': 'headline' }}
                  as="label"
                  weight="bold"
                  color="tertiary-900"
                  align="center"
                >
                  {t('meal-plan:step-1.age.label')}
                </Text>
                <Stack>
                  <TextInput
                    short
                    type="number"
                    min={0}
                    name="years"
                    defaultValue={
                      petProfile.birthDate
                        ? getDateDiffInYearsAndMonths(petProfile.birthDate).years
                        : undefined
                    }
                    onChange={handleAgeChange}
                    label={t('meal-plan:step-1.age.years')}
                    placeholder={t('meal-plan:step-1.age.years')}
                    displayLabel={false}
                    disabled={knownBirthDate}
                  />
                  <TextInput
                    short
                    type="number"
                    min={0}
                    name="months"
                    defaultValue={
                      petProfile.birthDate
                        ? getDateDiffInYearsAndMonths(petProfile.birthDate).months
                        : undefined
                    }
                    onChange={handleAgeChange}
                    label={t('meal-plan:step-1.age.months')}
                    placeholder={t('meal-plan:step-1.age.months')}
                    displayLabel={false}
                    disabled={knownBirthDate}
                  />
                </Stack>
              </Stack>

              <Stack
                direction="column"
                gap={{ '@initial': '1', '@bp2': '2' }}
                css={{ alignItems: 'center', marginTop: '0 !important' }}
              >
                <Checkbox
                  label={t('meal-plan:step-1.birth-date')}
                  onChange={() => setKnownBirthDate(!knownBirthDate)}
                  checked={knownBirthDate}
                />
                {knownBirthDate && (
                  <Stack gap={{ '@initial': '1', '@bp2': '3' }}>
                    <Select
                      isShort
                      label={t('meal-plan:step-1.mm')}
                      items={MONTHS_OPTIONS}
                      defaultValue={
                        petProfile.birthDate
                          ? format(petProfile.birthDate, 'MONTH_NUMBER')
                          : undefined
                      }
                      onChange={(month) => setBirthDate({ ...birthDate, month: parseInt(month) })}
                    />
                    <Select
                      isShort
                      label={t('meal-plan:step-1.dd')}
                      items={DAYS_OPTIONS}
                      defaultValue={
                        petProfile.birthDate ? format(petProfile.birthDate, 'DAY') : undefined
                      }
                      onChange={(day) => setBirthDate({ ...birthDate, day: parseInt(day) })}
                    />
                    <Select
                      isShort
                      label={t('meal-plan:step-1.yyyy')}
                      items={YEARS_OPTIONS}
                      defaultValue={
                        petProfile.birthDate ? format(petProfile.birthDate, 'YEAR') : undefined
                      }
                      onChange={(year) => setBirthDate({ ...birthDate, year: parseInt(year) })}
                    />
                  </Stack>
                )}
              </Stack>
            </Stack>

            <RadioGroup.Root
              label={t('meal-plan:step-1.gender')}
              name="gender"
              defaultValue={petProfile.gender}
              onValueChange={(value) =>
                setPetProfile({
                  ...petProfile,
                  gender: value as PetGenders,
                })
              }
            >
              <RadioOption
                value={PetGenders.MALE}
                label={t(`common:${PetGenders.MALE}`)}
                icon="male"
              />
              <RadioOption
                value={PetGenders.FEMALE}
                label={t(`common:${PetGenders.FEMALE}`)}
                icon="female"
              />
            </RadioGroup.Root>

            {petProfile.gender && (
              <RadioGroup.Root
                label={t(`meal-plan:step-1.spayed-neutered.${petProfile.gender}`)}
                name="isSpayed"
                defaultValue={petProfile.isSpayed ? '1' : '0'}
                onValueChange={(value) =>
                  setPetProfile({
                    ...petProfile,
                    isSpayed: value === '1',
                  })
                }
              >
                <Stack direction="row" css={{ flexWrap: 'nowrap' }}>
                  {' '}
                  <TextRadioOption value={'1'} label={t(`common:yes`)} />
                  <TextRadioOption value={'0'} label={t(`common:no`)} />
                </Stack>
              </RadioGroup.Root>
            )}

            <Button variant="primary" size="small" type="submit" disabled={!allFieldsFilled}>
              {t('common:continue')}
            </Button>
          </Stack>
        </MealPlanQuestionnaireCard>
      </Step1Root>
    </MealPlanLayout>
  )
}

Step1.displayName = 'Step1'
